import Blockquote from "@tiptap/extension-blockquote";
import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import Document from "@tiptap/extension-document";
import Heading, { HeadingOptions } from "@tiptap/extension-heading";
import History from "@tiptap/extension-history";
import Italic from "@tiptap/extension-italic";
import Link, { LinkOptions } from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Underline from "@tiptap/extension-underline";
import { Content, EditorContent, useEditor } from "@tiptap/react";

import GraphicOrganizerLink from "@/view/TiptapArticleBody/extensions/GraphicOrganizerLink";
import HeadingBlock from "@/view/TiptapArticleBody/extensions/HeadingBlock";
import {
  OrderedList as CustomOrderedList,
  OrderedListItem,
  UnorderedList,
  UnorderedListItem,
} from "@/view/TiptapArticleBody/extensions/List";
import { editorBodyStyles } from "./Editor.style";
import MenuBar from "./MenuBar";

interface EditorProps {
  content: Content;
  onChange: (content: Content) => void;
  config?: {
    Link?: Partial<LinkOptions>;
    Heading?: Partial<HeadingOptions>;
  };
}

const Editor = ({ content, onChange, config }: EditorProps) => {
  const defaultHeadingConfig: Partial<HeadingOptions> = {
    levels: [1, 2, 3],
  };
  const defaultLinkConfig: Partial<LinkOptions> = {
    /**
     * Prevents links from being extended to new text entered immediately after
     * a link, but it breaks links created by pasting a URL because of an
     * existing Tiptap bug.
     * @link https://github.com/ueberdosis/tiptap/issues/4420
     */
    autolink: false,
    openOnClick: false,
  };

  const editor = useEditor({
    extensions: [
      Blockquote,
      Bold,
      Document,
      Heading.configure(config?.Heading || defaultHeadingConfig),
      History,
      Italic,
      Link.configure(config?.Link || defaultLinkConfig),
      GraphicOrganizerLink,
      ListItem,
      OrderedList,
      BulletList,
      Paragraph,
      Text,
      Underline,
      CustomOrderedList,
      UnorderedList,
      OrderedListItem,
      UnorderedListItem,
      HeadingBlock,
    ],
    content,
    onUpdate: () => onChange(editor?.getHTML() || ""),
  });

  if (!editor) return null;

  return (
    <div className="wrapper" css={editorBodyStyles}>
      <MenuBar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};

export default Editor;
